/**
 * Active Loan Center — feature-local view-model helpers.
 *
 * Canonical stages, urgency, flag derivation, and segment predicate for the
 * redesigned active Loan Center.
 *
 * The stale/lock calculation (stale basis, days-stale, last-activity, lock
 * state, and the `isStalled` predicate) now lives in the shared service
 * `src/services/loanStaleness.ts` so the Loan Center AND the Pipeline report
 * compute "stalled" identically. Those symbols are re-exported below for the
 * existing consumers that import them from this module.
 *
 * The segment predicate here MUST stay behaviorally identical to the SQL
 * `p_segment` branch in `search_encompass_loans` — both are referenced by the
 * segment-predicate parity invariant. Changes must land in both places (and
 * the parity test must stay green).
 */
import { parseAsLocalDate } from '@/utils/dateHelpers';
import type { ProspectLoan, ProspectFilters } from './types';
import { deriveLockState, isStalled, LOCK_EXPIRING_WINDOW_DAYS } from '@/services/loanStaleness';

// Re-export the shared stale/lock helpers so existing Loan Center consumers
// (redesign/directions/*, redesign/primitives/*, and the tests) keep importing
// them from this module unchanged.
export {
  getStaleBasisDate,
  getDaysStale,
  getLastActivityDate,
  getDaysSinceLastActivity,
  deriveLockState
} from '@/services/loanStaleness';
export type { LockState } from '@/services/loanStaleness';

// ---------------------------------------------------------------------------
// Canonical stages
//
// The active Loan Center intentionally omits a `lead` stage. The real active
// pipeline begins at `application`; lead/prospect work is excluded from the
// `!isPostClosing` dataset. Do not reintroduce a lead column.
// ---------------------------------------------------------------------------

export type CanonicalStage =
  | 'application'
  | 'processing'
  | 'underwriting'
  | 'approved'
  | 'clear_to_close'
  | 'funded';

export const CANONICAL_STAGES: readonly CanonicalStage[] = [
  'application',
  'processing',
  'underwriting',
  'approved',
  'clear_to_close',
  'funded'
] as const;

export const CANONICAL_STAGE_LABELS: Record<CanonicalStage, string> = {
  application: 'Application',
  processing: 'Processing',
  underwriting: 'Underwriting',
  approved: 'Approved',
  clear_to_close: 'Clear to Close',
  funded: 'Funded'
};

/**
 * Lookup table for mapping Encompass milestone strings onto canonical stages.
 * Keys are lowercased for case-insensitive matching.
 */
const MILESTONE_TO_STAGE: Record<string, CanonicalStage> = {
  // application
  started: 'application',
  'file started': 'application',
  'loan setup': 'application',
  // processing
  processing: 'processing',
  'sent to processing': 'processing',
  // underwriting
  submitted: 'underwriting',
  resubmitted: 'underwriting',
  // approved
  'cond. approval': 'approved',
  'approved w/ cond.': 'approved',
  // clear_to_close
  'clear to close': 'clear_to_close',
  'docs sent': 'clear_to_close',
  'final approval': 'clear_to_close',
  'docs out': 'clear_to_close',
  funding: 'clear_to_close',
  completion: 'clear_to_close',
  // funded
  funded: 'funded',
  purchased: 'funded'
};

/**
 * Map an Encompass milestone string (or a loan) to a canonical stage.
 *
 * Precedence:
 * 1. If the loan has a `funded_date`, it is `funded` regardless of milestone text.
 * 2. Otherwise look up the milestone string (case-insensitive).
 * 3. Unknown strings fall back to `application`.
 */
export function mapToCanonicalStage(input: string | ProspectLoan | null | undefined): CanonicalStage {
  if (!input) {
return 'application';
}
  if (typeof input !== 'string') {
    if (input.funded_date) {
return 'funded';
}
    return mapToCanonicalStage(input.milestone);
  }
  const key = input.trim().toLowerCase();
  return MILESTONE_TO_STAGE[key] ?? 'application';
}

/**
 * Canonical index (0-based) of a stage, used for MilestoneTrack progress fills.
 */
export function canonicalStageIndex(stage: CanonicalStage): number {
  return CANONICAL_STAGES.indexOf(stage);
}

// ---------------------------------------------------------------------------
// Days-in-stage + urgency
// ---------------------------------------------------------------------------

function differenceInCalendarDays(later: Date, earlier: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  const a = new Date(later.getFullYear(), later.getMonth(), later.getDate()).getTime();
  const b = new Date(earlier.getFullYear(), earlier.getMonth(), earlier.getDate()).getTime();
  return Math.round((a - b) / msPerDay);
}

/**
 * Days since the loan entered its current milestone. Null if `milestone_date`
 * is absent or unparseable. Parses date-only strings as local time to avoid
 * UTC timezone shift, matching the existing `ProspectTable.getDaysInMilestone`.
 */
export function getDaysInMilestone(loan: Pick<ProspectLoan, 'milestone_date'>): number | null {
  if (!loan.milestone_date) {
return null;
}
  const date = parseAsLocalDate(loan.milestone_date);
  if (isNaN(date.getTime())) {
return null;
}
  return differenceInCalendarDays(new Date(), date);
}

export type Urgency = 'ok' | 'warn' | 'urgent';

/**
 * Urgency tier for days-in-stage. Thresholds chosen for mortgage-loan
 * processing cadence, which tolerates short stays in any milestone.
 */
export function deriveUrgency(daysInMilestone: number | null): Urgency {
  if (daysInMilestone === null) {
return 'ok';
}
  if (daysInMilestone >= 7) {
return 'urgent';
}
  if (daysInMilestone >= 3) {
return 'warn';
}
  return 'ok';
}

// ---------------------------------------------------------------------------
// Derived activity flag
//
// The Flag column is intentionally activity-only. Lock state and its timing
// belong in the Lock column; a lock that is active, expiring, or expired never
// replaces the stalled/activity monitor.
//
// The stalled rule is shared in `src/services/loanStaleness.ts` (parity mirror:
// the SQL `p_segment` branch). No `high_value` rule in this phase.
// ---------------------------------------------------------------------------

export type RiskFlag = 'stalled';

export function deriveFlag(loan: ProspectLoan): RiskFlag | null {
  return isStalled(loan) ? 'stalled' : null;
}

function isLockExpiring(lockState: ReturnType<typeof deriveLockState>): boolean {
  return lockState.isCurrentlyLocked
    && lockState.daysUntilExpiration !== null
    && lockState.daysUntilExpiration >= 0
    && lockState.daysUntilExpiration <= LOCK_EXPIRING_WINDOW_DAYS;
}

// ---------------------------------------------------------------------------
// Segment predicate
//
// Mirrors the SQL `p_segment` branch in `search_encompass_loans`. Both must
// stay behaviorally identical — the segment-predicate parity test asserts this.
//   locked     → isCurrentlyLocked (or lock met)
//   expiring   → active lock expires within 5 days
//   at_risk    → expired lock OR expiring lock OR stalled activity
//   funded     → canonical stage 'funded' (or funded_date present)
//   all        → everything
//   avg        → everything (informational only — chip highlights but does not narrow)
// ---------------------------------------------------------------------------

export type Segment = NonNullable<ProspectFilters['segment']>;

export function matchesSegment(loan: ProspectLoan, segment: Segment | null | undefined): boolean {
  if (!segment || segment === 'all' || segment === 'avg') {
return true;
}

  if (segment === 'locked') {
    const { isCurrentlyLocked, isLockMet } = deriveLockState(loan);
    return isCurrentlyLocked || isLockMet;
  }

  if (segment === 'expiring') {
    return isLockExpiring(deriveLockState(loan));
  }

  if (segment === 'at_risk') {
    const lockState = deriveLockState(loan);
    return lockState.isLockExpired || isLockExpiring(lockState) || isStalled(loan);
  }

  if (segment === 'funded') {
    return mapToCanonicalStage(loan) === 'funded';
  }

  return true;
}
